'use client';
import { useI18n } from "@/locales/client";
import Image from "next/image";

interface HeroProductsProps {
  background: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  description?: React.ReactNode;
  buttons?: React.ReactNode;
}

const HeroProducts: React.FC<HeroProductsProps> = ({
  background,
  title,
  subtitle,
  description,
  buttons,
}) => {
  const t = useI18n();
  const isVideo = background.endsWith('.webm') || background.endsWith('.mp4');

  return (
    <section className="relative w-full">
      <div className="relative h-[640px] md:h-[560px] xl:h-[550px] 2xl:h-[580px] 3xl:h-[600px]">
        {isVideo ? (
          <video
            src={background}
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <Image
            src={background}
            alt={t('industrial-background')}
            aria-hidden="true"
            fill
            sizes="100vw"
            className="object-cover object-center"
            priority
            quality={100}
          />
        )}
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="container relative mx-auto flex h-full flex-col justify-center px-4 xl:px-8 2xl:px-10 text-left 3xl:px-[150px]">
          <h1 className="font-sans text-4xl leading-tight text-gray-50 md:text-5xl lg:w-[627px] lg:text-[54px] xl:w-[680px] xl:text-[58px] 2xl:w-[700px] 2xl:text-[60px] 3xl:text-[64px] 3xl:w-[750px]">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-5 max-w-[628px] font-['Poppins',Helvetica] text-2xl text-white md:text-3xl lg:mt-2 lg:text-[32px] xl:text-[34px] xl:max-w-[680px] 2xl:text-[35px] 2xl:max-w-[700px] 3xl:text-[36px] 3xl:max-w-[750px]">
              {subtitle}
            </p>
          )}
          {description && (
            <p className="mt-6 max-w-[700px] font-['Poppins',Helvetica] text-base text-white lg:mt-2 lg:text-[18px] lg:leading-loose xl:text-[19px] xl:max-w-[750px] 2xl:text-[19.5px] 2xl:max-w-[780px] 3xl:text-xl 3xl:max-w-[800px]">
              {description}
            </p>
          )}
          {buttons && (
            <div className="mt-8 mb-6 flex flex-wrap gap-4 xl:mt-9 xl:mb-0 xl:gap-5 2xl:mt-10 2xl:gap-6">
              {buttons}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default HeroProducts;
